/**
 * 애플리케이션 라우터 설정
 *
 * 주요 기능:
 * - React Router v6 기반 클라이언트 사이드 라우팅
 * - 코드 스플리팅을 통한 초기 번들 크기 최적화
 * - 인증이 필요한 페이지에 대한 보호된 라우트 적용
 * - Suspense 기반 지연 로딩 UI 제공
 *
 * 코드 스플리팅 전략:
 * - 즉시 로드: HomePage, 에러 페이지 (First Paint 최적화)
 * - 지연 로드: 나머지 모든 페이지 (초기 번들 크기 감소)
 *
 * 사용처: App.tsx에서 RouterProvider로 제공
 */

import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { LoadingState } from "@/components/common/LoadingState";
import Layout from "@/components/layout/Layout";
// 즉시 로드 페이지 - First Paint 최적화를 위해 코드 스플리팅 미적용
import HomePage from "@/pages/HomePage";
import NotFoundPage from "@/pages/NotFoundPage";
import ServerErrorPage from "@/pages/ServerErrorPage";

// 지연 로드 페이지 - 초기 번들 크기 최적화를 위해 코드 스플리팅 적용
const ExplorePage = lazy(() => import("@/pages/ExplorePage"));
const ArtistPage = lazy(() => import("@/pages/ArtistPage"));
const ArtistDetailPage = lazy(() => import("@/pages/ArtistDetailPage"));
const ProductDetailPage = lazy(() => import("@/pages/ProductDetailPage"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const MyPage = lazy(() => import("@/pages/MyPage"));
const PurchasedPage = lazy(() => import("@/pages/PurchasedPage"));
const CertificatePage = lazy(() => import("@/pages/CertificatePage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentFailurePage = lazy(() => import("@/pages/PaymentFailurePage"));
const GoogleCallback = lazy(() =>
  import("@/pages/auth/GoogleCallback").then((module) => ({
    default: module.GoogleCallback,
  })),
);
const PlaylistPage = lazy(() => import("@/pages/PlaylistPage"));
const VerifyPage = lazy(() => import("@/pages/VerifyPage"));
const WatermarkVisualizerPage = lazy(
  () => import("@/pages/WatermarkVisualizerPage"),
);

interface SuspenseWrapperProps {
  children: React.ReactNode;
  fallbackMessage?: string;
}

/**
 * Suspense Wrapper 컴포넌트
 *
 * 지연 로드되는 컴포넌트의 로딩 상태를 일관된 UI로 표시
 *
 * @param children - 지연 로드될 React 컴포넌트
 * @param fallbackMessage - 로딩 중 표시할 메시지
 * @returns Suspense로 감싼 컴포넌트
 */
const SuspenseWrapper = ({
  children,
  fallbackMessage,
}: SuspenseWrapperProps) => (
  <Suspense
    fallback={
      <LoadingState message={fallbackMessage} minHeight="calc(100vh - 200px)" />
    }
  >
    {children}
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <ServerErrorPage />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        // 비밀번호 재설정 경로(이메일에서 유입)
        path: "password/reset/confirm/:uid/:token",
        element: <HomePage />, // HomePage로 들어가서 모달로 처리
      },
      {
        path: "explore",
        element: (
          <SuspenseWrapper fallbackMessage="Loading Explore...">
            <ExplorePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "artist",
        element: (
          <SuspenseWrapper fallbackMessage="Loading Artists...">
            <ArtistPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "mypage",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper fallbackMessage="Loading My Page...">
              <MyPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "cart",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper fallbackMessage="Loading Cart...">
              <CartPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "certificate/:orderItemId",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper fallbackMessage="Loading Certificate...">
              <CertificatePage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "product/:productId",
        element: (
          <SuspenseWrapper fallbackMessage="Loading Product...">
            <ProductDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "artist/:artistId",
        element: (
          <SuspenseWrapper fallbackMessage="Loading Artist...">
            <ArtistDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "payment/success",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper fallbackMessage="Processing Payment...">
              <PaymentSuccessPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "payment/failure",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper fallbackMessage="Processing...">
              <PaymentFailurePage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "auth/google/callback",
        element: (
          <SuspenseWrapper fallbackMessage="Authenticating...">
            <GoogleCallback />
          </SuspenseWrapper>
        ),
      },
      {
        path: "purchased",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper fallbackMessage="Loading Purchases...">
              <PurchasedPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "playlist",
        element: (
          <SuspenseWrapper fallbackMessage="Loading Playlist...">
            <PlaylistPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "verify",
        element: (
          <SuspenseWrapper fallbackMessage="Loading Verify Page...">
            <VerifyPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "watermark",
        element: (
          <SuspenseWrapper fallbackMessage="Loading Watermark Map...">
            <WatermarkVisualizerPage />
          </SuspenseWrapper>
        ),
      },
      // 에러 페이지
      {
        path: "500",
        element: <ServerErrorPage />,
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
]);
